import { useEffect, useRef } from "react";
import {
  claimOncePerSession,
  sessionScopedEventId,
} from "../../../tools/analytics/eventIdentity";
import { enqueueImpression, type ImpressionPayload } from "./impressionBatcher";

// Recommender v2 — Phase 0.3 (client). Attach the returned ref to a feed card;
// when the card stays >=1s in view (and the tab is visible) it reports exactly
// one "seen" impression for this SESSION. Re-entering the viewport (scroll-back)
// does NOT re-report, and neither does a remount — opening a card and coming
// back is one card seen once, and counting it per mount impressed one card four
// times in a 40-second local session. This is the genuine "seen" signal (vs
// server-side "served"). useEffect/useRef live here in a dedicated hook (UI
// machinery), never in a business component.

const DWELL_THRESHOLD_MS = 1000;

/**
 * A union and not a flat record with a nullable uid, because the practice
 * surface is the only one that HAS a corpus and it always has one: a reader
 * holding six overlapping practices reads one of them at a time, and which one
 * is unrecoverable once the row is written. Every other surface must not carry
 * the field at all, which is what makes forgetting it on the practice feed a
 * type error rather than 797 rows silently credited to the aggregate.
 */
type Args = {
  contentKind: ImpressionPayload["contentKind"];
  contentId: string;
} & (
  | {
      surface: "practice";
      /** `null` is the "All practices" aggregate — a union of the reader's
       * profiles and not one of them. `practiceRailProfileUid` mints it. */
      practiceProfileUid: string | null;
    }
  | { surface: Exclude<ImpressionPayload["surface"], "practice"> }
);

export function useViewportImpression(
  args: Args
): (node: HTMLElement | null) => void {
  const { contentKind, contentId, surface } = args;
  const practiceProfileUid =
    args.surface === "practice" ? args.practiceProfileUid : null;
  const nodeRef = useRef<HTMLElement | null>(null);
  // Everything that makes this a distinct card being seen, and nothing that
  // makes it a distinct MOUNT. It is both the fire-once key and, seeded per
  // page load, the `sourceEventId` — one string, because the two drifting apart
  // is how `skipDuplicates` came to dedup nothing.
  const fact = `impression:${surface}:${
    practiceProfileUid === null ? "-" : practiceProfileUid
  }:${contentKind}:${contentId}`;

  useEffect(() => {
    const node = nodeRef.current;
    if (node === null) return;

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    const clearDwell = (): void => {
      if (dwellTimer !== null) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry === undefined) return;
        const visible =
          entry.isIntersecting && document.visibilityState === "visible";
        if (visible) {
          if (dwellTimer !== null) return;
          dwellTimer = setTimeout(() => {
            dwellTimer = null;
            if (!claimOncePerSession(fact)) return;
            enqueueImpression({
              contentKind,
              contentId,
              surface,
              practiceProfileUid,
              // The card was visible at least this long; full-dwell measurement
              // is a later refinement.
              dwellMs: DWELL_THRESHOLD_MS,
              sourceEventId: sessionScopedEventId(fact),
            });
          }, DWELL_THRESHOLD_MS);
        } else {
          clearDwell();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
    return () => {
      clearDwell();
      observer.disconnect();
    };
  }, [fact, contentKind, contentId, surface, practiceProfileUid]);

  // Ref callback: keep nodeRef in sync so the observer effect can read the node.
  return (node: HTMLElement | null) => {
    nodeRef.current = node;
  };
}
